import React, { useState, useEffect, useRef } from 'react';
import { 
  X, Sparkles, UploadCloud, FileText, Image as ImageIcon, 
  CheckCircle2, AlertCircle, AlertTriangle, Loader2, ArrowRight, ArrowLeft,
  Check, Trash2, Edit3, ShieldAlert, Plus, RefreshCw, Cpu, Layers, BookOpen, 
  Minus, CheckCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '../ui/Button';
import { QuestionSet } from '../../types';
import { getGeminiModelsConfigApi } from '../../services/questionService';
import { 
  uploadImageFilesForAi, 
  uploadDocFilesForAi, 
  formatFileSize,
  MediaLibraryItem 
} from '../../services/mediaService';
import { AiHeavyJobPayload } from '../../services/aiHeavyJobService';
import { getFriendlyErrorMessage } from '../../utils/aiErrorUtils';
import { AiMediaLibraryModal } from './AiMediaLibraryModal';
import { useAuth } from '../../AuthProvider';
import { useAiHeavyJob } from '../../context/AiHeavyJobContext';

export interface AttachedMediaFile {
  id: string;
  name: string;
  size: number;
  type: 'image' | 'doc';
  url: string;
  source: 'upload' | 'library';
}

interface AiGenerateQuestionsModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  targetSet?: QuestionSet | null;
  onSuccess?: (addedCount: number) => void;
  onQuotaExceeded?: (message?: string) => void;
  onTriggerBackgroundJob?: (job: { jobId: string; params: any }) => void;
}

export const AiGenerateQuestionsModal: React.FC<AiGenerateQuestionsModalProps> = ({
  isOpen: propIsOpen,
  onClose: propOnClose,
  targetSet: propTargetSet,
  onSuccess,
  onQuotaExceeded
}) => {
  const { userProfile } = useAuth();
  const {
    currentStep,
    setCurrentStep,
    activeJob,
    jobEvent,
    isStartingJob,
    isProcessing,
    isCompleted,
    isFailed,
    isModalOpen: ctxIsModalOpen,
    isMinimized,
    completedQuestions,
    targetSet: ctxTargetSet,
    startJob,
    closeModal,
    minimizeModal,
    dismissJob,
    resetToInput,
    updateQuestionText,
    updateOptionText,
    selectCorrectOption,
    updateExplanation,
    updateDifficulty,
    deleteQuestion,
    saveQuestionsToSet
  } = useAiHeavyJob();

  const currentSet = propTargetSet || ctxTargetSet;

  // Dynamic Models Config from Backend
  const [availableModels, setAvailableModels] = useState<string[]>([
    'gemini-2.5-flash',
    'gemini-1.5-flash',
    'gemini-1.5-pro',
    'gemini-2.0-flash'
  ]);
  const [selectedModel, setSelectedModel] = useState<string>('gemini-2.5-flash');

  // Input states
  const [promptText, setPromptText] = useState<string>('');
  const [questionCount, setQuestionCount] = useState<number>(5);
  const [difficulty, setDifficulty] = useState<string>('Hỗn hợp');
  
  // Attached files (Static URLs only)
  const [selectedFiles, setSelectedFiles] = useState<AttachedMediaFile[]>([]);
  const [uploadingMedia, setUploadingMedia] = useState<boolean>(false);

  // 2-Tabs Media Library Modal
  const [isLibraryModalOpen, setIsLibraryModalOpen] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const vipTier = userProfile?.vipTier || 'USER';
  const limit = vipTier === 'SVIP' ? 300 : vipTier === 'VIP' ? 200 : 100;
  const currentQuestionCount = currentSet?.questionCount || currentSet?.questions?.length || 0;
  const maxAllowed = Math.max(0, limit - currentQuestionCount);

  const isVisible = (propIsOpen !== undefined ? propIsOpen : ctxIsModalOpen) && !isMinimized;

  // Fetch Available AI Models
  useEffect(() => {
    if (!isVisible) return;

    const fetchModelsConfig = async () => {
      try {
        const config = await getGeminiModelsConfigApi();
        if (config && Array.isArray(config.models) && config.models.length > 0) {
          setAvailableModels(config.models);
          setSelectedModel(config.model || config.models[0]);
        }
      } catch (e) {
        console.warn("Dùng cấu hình model mặc định:", e);
      }
    };

    fetchModelsConfig();
  }, [isVisible]);

  const handleClose = () => {
    if (propOnClose) {
      propOnClose();
    }
    closeModal();
  };

  if (!isVisible) return null;

  // Xử lý đính kèm file mới
  const handleFilesChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    setErrorMessage(null);
    setUploadingMedia(true);

    const imageFiles: File[] = [];
    const docFiles: File[] = [];

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      const isImage = file.type.startsWith('image/') || /\.(png|jpe?g|webp|gif|svg)$/i.test(file.name);
      if (isImage) {
        imageFiles.push(file);
      } else {
        docFiles.push(file);
      }
    }

    try {
      const newAttachedFiles: AttachedMediaFile[] = [];

      // 1. Upload ảnh
      if (imageFiles.length > 0) {
        const imgUrls = await uploadImageFilesForAi(imageFiles);
        imageFiles.forEach((f, idx) => {
          const url = imgUrls[idx] || imgUrls[0] || '';
          if (url) {
            newAttachedFiles.push({
              id: `upload_img_${Date.now()}_${idx}`,
              name: f.name,
              size: f.size,
              type: 'image',
              url,
              source: 'upload'
            });
          }
        });
      }

      // 2. Upload tài liệu (Docs/PDF)
      if (docFiles.length > 0) {
        const docUrls = await uploadDocFilesForAi(docFiles);
        docFiles.forEach((f, idx) => {
          const url = docUrls[idx] || docUrls[0] || '';
          if (url) {
            newAttachedFiles.push({
              id: `upload_doc_${Date.now()}_${idx}`,
              name: f.name,
              size: f.size,
              type: 'doc',
              url,
              source: 'upload'
            });
          }
        });
      }

      if (newAttachedFiles.length > 0) {
        setSelectedFiles(prev => [...prev, ...newAttachedFiles]);
        toast.success(`Đã tải lên và tạo link tĩnh cho ${newAttachedFiles.length} file thành công!`);
      }
    } catch (err: any) {
      console.error("Lỗi khi tải file media:", err);
      const friendly = getFriendlyErrorMessage(err);
      setErrorMessage(friendly);
      toast.error(friendly);
    } finally {
      setUploadingMedia(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Tái sử dụng từ Thư viện
  const handleSelectFromLibrary = (libraryItems: MediaLibraryItem[]) => {
    const newlyAdded: AttachedMediaFile[] = [];
    
    libraryItems.forEach((item, idx) => {
      const alreadyHas = selectedFiles.some(f => f.url === item.url);
      if (!alreadyHas) {
        newlyAdded.push({
          id: `lib_${Date.now()}_${idx}_${item.id}`,
          name: item.name || item.fileName,
          size: item.size,
          type: item.type,
          url: item.url,
          source: 'library'
        });
      }
    });

    if (newlyAdded.length > 0) {
      setSelectedFiles(prev => [...prev, ...newlyAdded]);
      toast.success(`Đã chọn ${newlyAdded.length} tài liệu từ thư viện.`);
    }
  };

  const handleRemoveFile = (fileId: string) => {
    setSelectedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleClearAllFiles = () => {
    setSelectedFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Khởi chạy tiến trình AI duy nhất (Heavy Background Task)
  const handleStartGeneration = async () => {
    if (isStartingJob || uploadingMedia) return;

    const rawPrompt = typeof promptText === 'string' ? promptText.trim() : '';
    if (!rawPrompt && selectedFiles.length === 0) {
      setErrorMessage('Vui lòng nhập chủ đề / nội dung hoặc chọn file tài liệu tham khảo.');
      return;
    }

    const safeCount = Math.max(1, Math.min(maxAllowed > 0 ? maxAllowed : 5, Number(questionCount) || 5));
    if (maxAllowed > 0 && currentQuestionCount + safeCount > limit) {
      toast.error(`Chỉ có thể tạo tối đa ${maxAllowed} câu hỏi nữa để không vượt quá hạn mức tài khoản.`);
      return;
    }

    const cleanModel = typeof selectedModel === 'string' && selectedModel.trim() 
      ? selectedModel.trim() 
      : (availableModels[0] || 'gemini-2.5-flash');

    const staticFileUrls: string[] = Array.isArray(selectedFiles)
      ? selectedFiles.map(f => (typeof f?.url === 'string' ? f.url.trim() : '')).filter(Boolean)
      : [];

    const targetSetIdStr = currentSet?.id ? String(currentSet.id) : (currentSet?._id ? String(currentSet._id) : '');

    const userPrompt = rawPrompt || 'Tạo các câu hỏi trắc nghiệm chất lượng cao bám sát nội dung tài liệu đính kèm.';

    // Bước 1: Thêm System Prompt cực kỳ nghiêm ngặt bắt buộc AI trả về JSON chuẩn
    const finalPrompt = `${userPrompt}

QUAN TRỌNG: Bạn BẮT BUỘC phải trả về kết quả dưới dạng một mảng JSON thuần túy (không bọc trong markdown \`\`\`json). Cấu trúc của mỗi object trong mảng phải ĐÚNG CHUẨN như sau:
[
  {
    "question": "Nội dung câu hỏi",
    "options": ["Đáp án A", "Đáp án B", "Đáp án C", "Đáp án D"],
    "correctAnswer": 0, // Vị trí index của đáp án đúng (0 đến 3)
    "explanation": "Giải thích tại sao đáp án đó đúng",
    "level": "Thông hiểu" // Có thể là Nhận biết, Thông hiểu, Vận dụng, Vận dụng cao
  }
]`;

    // Đảm bảo payload chỉ chứa kiểu dữ liệu nguyên thủy (Primitive Types)
    const payload: AiHeavyJobPayload = {
      prompt: finalPrompt,
      fileUrls: staticFileUrls,
      fileUrl: staticFileUrls[0] || undefined,
      requestedModel: cleanModel,
      subject: typeof currentSet?.subject === 'string' ? currentSet.subject : String(currentSet?.subject || ''),
      grade: typeof currentSet?.grade === 'string' ? currentSet.grade : String(currentSet?.grade || ''),
      difficulty: typeof difficulty === 'string' ? difficulty : 'Hỗn hợp',
      questionCount: safeCount,
      targetSetId: targetSetIdStr
    };

    setErrorMessage(null);
    try {
      await startJob(payload, currentSet);
    } catch (err: any) {
      const friendly = getFriendlyErrorMessage(err);
      setErrorMessage(friendly);
    }
  };

  // Lưu câu hỏi vào bộ câu hỏi
  const handleSaveToSet = async () => {
    setIsSaving(true);
    try {
      const ok = await saveQuestionsToSet(currentSet?.id, (count) => {
        if (onSuccess) {
          onSuccess(count);
        }
        if (propOnClose) {
          propOnClose();
        }
      });
      if (ok) {
        // Clear input state for next time
        setPromptText('');
        setSelectedFiles([]);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const progress = Math.min(100, Math.max(0, jobEvent?.progress || (isCompleted ? 100 : 15)));

  // Yêu cầu 1: Ẩn hoàn toàn Modal khi isMinimized = true hoặc khi modal không mở
  if (!isVisible) {
    return null;
  }

  return (
    // closeOnOverlayClick={false}: Khóa tính năng bấm ra ngoài overlay để tránh mất dữ liệu đang làm dở
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-xs animate-in fade-in duration-200 overflow-y-auto"
      onClick={(e) => {
        // Ngăn chặn sự kiện click overlay đóng modal
        e.stopPropagation();
      }}
    >
      <div 
        className={`bg-white rounded-2xl w-full shadow-2xl border border-slate-200 relative my-6 transition-all overflow-hidden ${
          currentStep === 'result' ? 'max-w-4xl' : 'max-w-xl'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/80">
          <div className="flex items-center space-x-3 min-w-0">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-md shrink-0 ${
              currentStep === 'result' 
                ? 'bg-gradient-to-tr from-emerald-600 to-teal-600 text-white' 
                : currentStep === 'processing' 
                  ? 'bg-gradient-to-tr from-purple-600 to-indigo-600 text-white animate-pulse'
                  : 'bg-gradient-to-tr from-purple-600 to-indigo-600 text-white'
            }`}>
              {currentStep === 'result' ? (
                <Check className="w-5 h-5 stroke-[2.5]" />
              ) : currentStep === 'processing' ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Sparkles className="w-5 h-5" />
              )}
            </div>

            <div className="min-w-0">
              <div className="flex items-center space-x-2">
                <h3 className="text-base sm:text-lg font-bold text-slate-900 truncate">
                  {currentStep === 'result' 
                    ? 'Nghiệm thu & Lưu câu hỏi AI' 
                    : currentStep === 'processing' 
                      ? 'Gemini đang tạo câu hỏi...' 
                      : 'Tạo câu hỏi trắc nghiệm bằng AI'}
                </h3>
                <span className="px-2.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-bold font-mono rounded-full border border-purple-200/60 flex items-center space-x-1 shrink-0">
                  <Cpu className="w-3 h-3 text-purple-600" />
                  <span>{selectedModel}</span>
                </span>
              </div>
              <p className="text-xs text-slate-500 truncate">
                {currentSet ? (
                  <>Môn {currentSet.subject} - Lớp {currentSet.grade} • Bộ: <span className="font-semibold text-slate-700">{currentSet.name}</span></>
                ) : 'Tạo câu hỏi bám sát chuẩn kiến thức & tài liệu'}
              </p>
            </div>
          </div>

          {/* Action buttons on header: Giữ duy nhất nút [-] Thu nhỏ ở Header cạnh nút X */}
          <div className="flex items-center space-x-1.5 shrink-0">
            <button
              type="button"
              onClick={minimizeModal}
              className="p-2 text-slate-500 hover:text-purple-700 hover:bg-purple-100/60 rounded-xl transition-colors cursor-pointer flex items-center space-x-1 border border-slate-200/80 bg-white shadow-2xs text-xs font-semibold"
              title="Thu nhỏ xuống góc màn hình để làm việc khác"
            >
              <Minus className="w-4 h-4" />
              <span className="hidden sm:inline text-[11px]">Thu nhỏ</span>
            </button>

            <button
              type="button"
              onClick={handleClose}
              className="text-slate-400 hover:text-slate-700 p-2 rounded-xl hover:bg-slate-200/60 transition-colors cursor-pointer"
              title="Đóng hộp thoại"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* TRẠNG THÁI 1: MODAL CHỜ (Đang tạo câu hỏi trong nền) */}
        {currentStep === 'processing' ? (
          <div className="p-6 sm:p-8 space-y-6 bg-gradient-to-b from-purple-50/40 via-white to-white">
            <div className="text-center max-w-md mx-auto space-y-3">
              <div className="relative inline-flex items-center justify-center">
                <div className="w-16 h-16 rounded-2xl bg-purple-100 text-purple-600 flex items-center justify-center animate-bounce">
                  <Sparkles className="w-8 h-8" />
                </div>
                <div className="absolute inset-0 rounded-2xl border-4 border-purple-400 border-t-transparent animate-spin" />
              </div>

              <div>
                <h4 className="text-base font-bold text-slate-900">
                  {jobEvent?.message || 'Gemini đang xử lý và tổng hợp câu hỏi...'}
                </h4>
                <p className="text-xs text-slate-600 mt-2 leading-relaxed font-medium bg-purple-50/70 p-3 rounded-xl border border-purple-200/60">
                  ✨ <strong>Gemini đang phân tích tài liệu và tạo câu hỏi.</strong> Bạn vui lòng đợi trong giây lát hoặc có thể nhấn nút <strong>Thu nhỏ</strong> ở góc trên để tiếp tục làm việc khác.
                </p>
              </div>

              {/* Progress Bar */}
              <div className="pt-2">
                <div className="flex items-center justify-between text-xs mb-1.5 font-bold">
                  <span className="text-slate-500">Tiến trình AI</span>
                  <span className="text-purple-700 font-mono">{progress}%</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden p-0.5 border border-slate-200">
                  <div 
                    className="h-full bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : currentStep === 'result' ? (
          <div className="p-5 sm:p-6 space-y-4">
            <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 p-3.5 rounded-xl">
              <div className="flex items-center space-x-2 text-emerald-900 text-xs font-semibold">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                <span>
                  Đã tạo thành công <strong>{completedQuestions.length} câu hỏi</strong>. Bạn có thể đọc, tinh chỉnh đáp án và giải thích bên dưới trước khi lưu.
                </span>
              </div>
              <span className="text-xs font-mono font-bold text-emerald-800 bg-emerald-100 px-2.5 py-1 rounded-lg">
                {completedQuestions.length} câu
              </span>
            </div>

            {/* Questions List for Inline Editing */}
            <div className="max-h-[55vh] overflow-y-auto space-y-4 pr-1">
              {completedQuestions.map((q, idx) => (
                <div key={idx} className="p-4 rounded-xl border border-slate-200 bg-slate-50/70 hover:border-purple-300 transition-all space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center space-x-2">
                      <span className="px-2.5 py-1 bg-purple-600 text-white text-xs font-bold rounded-lg shrink-0">
                        Câu {idx + 1}
                      </span>
                      <select
                        value={q.difficulty || 'Thông hiểu'}
                        onChange={(e) => updateDifficulty(idx, e.target.value)}
                        className="text-[11px] font-semibold bg-white border border-slate-200 rounded-lg py-1 px-2.5 text-slate-700 focus:outline-none focus:border-purple-600"
                      >
                        <option value="Nhận biết">Nhận biết</option>
                        <option value="Thông hiểu">Thông hiểu</option>
                        <option value="Vận dụng">Vận dụng</option>
                        <option value="Vận dụng cao">Vận dụng cao</option>
                      </select>
                    </div>

                    <button
                      type="button"
                      onClick={() => deleteQuestion(idx)}
                      title="Xóa câu hỏi này"
                      className="p-1.5 text-slate-400 hover:text-rose-600 rounded-lg hover:bg-rose-50 transition-colors cursor-pointer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Question Text */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">
                      Nội dung câu hỏi:
                    </label>
                    <textarea
                      rows={2}
                      value={q.questionText}
                      onChange={(e) => updateQuestionText(idx, e.target.value)}
                      placeholder="Nội dung câu hỏi..."
                      className="w-full bg-white border border-slate-300 rounded-lg p-2.5 text-xs sm:text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-600"
                    />
                  </div>

                  {/* Options */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                    {(q.options || []).map((opt, optIdx) => {
                      const isCorrect = Boolean(opt.isCorrect);
                      const keyLabel = opt.key || String.fromCharCode(65 + optIdx);
                      return (
                        <div 
                          key={optIdx}
                          onClick={() => selectCorrectOption(idx, optIdx)}
                          className={`p-2.5 rounded-xl text-xs flex items-center space-x-2 border transition-all cursor-pointer ${
                            isCorrect 
                              ? 'bg-emerald-50 border-emerald-400 ring-2 ring-emerald-500/20' 
                              : 'bg-white border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <button
                            type="button"
                            className={`w-6 h-6 rounded-lg font-bold text-[11px] shrink-0 flex items-center justify-center transition-colors ${
                              isCorrect ? 'bg-[#236539] text-white shadow-xs' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                            }`}
                          >
                            {keyLabel}
                          </button>
                          <input
                            type="text"
                            value={opt.text}
                            onChange={(e) => {
                              e.stopPropagation();
                              updateOptionText(idx, optIdx, e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            placeholder={`Đáp án ${keyLabel}...`}
                            className="w-full bg-transparent text-xs text-slate-900 focus:outline-none font-medium"
                          />
                          {isCorrect && (
                            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Explanation */}
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-500 mb-1">
                      Giải thích / Lời giải chi tiết:
                    </label>
                    <input
                      type="text"
                      value={q.explanation || ''}
                      onChange={(e) => updateExplanation(idx, e.target.value)}
                      placeholder="Giải thích vì sao chọn đáp án này..."
                      className="w-full bg-white border border-slate-200 rounded-lg py-1.5 px-2.5 text-xs text-slate-700 focus:outline-none focus:border-purple-600"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Footer Buttons for State 4 */}
            <div className="p-4 border-t border-slate-100 bg-slate-50/70 -mx-5 sm:-mx-6 -mb-5 sm:-mb-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <div className="flex items-center space-x-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={resetToInput}
                  disabled={isSaving}
                  className="text-xs cursor-pointer"
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-1" />
                  Tạo lại từ đầu
                </Button>
              </div>

              <div className="flex items-center space-x-2 justify-end">
                <Button
                  type="button"
                  onClick={handleSaveToSet}
                  disabled={isSaving || completedQuestions.length === 0}
                  className="bg-[#236539] hover:bg-[#1b4f2c] text-white font-bold shadow-md cursor-pointer flex items-center space-x-1.5 py-2.5 px-5 text-xs sm:text-sm"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-1" />
                      <span>Đang lưu vào bộ...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-1" />
                      <span>Lưu vào Bộ câu hỏi ({completedQuestions.length})</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          /* TRẠNG THÁI KHỞI ĐẦU: FORM NHẬP LIỆU (currentStep === 'input') */
          <div className="p-5 space-y-4">
            {/* Error Banner */}
            {errorMessage && (
              <div className="p-3.5 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-xl flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span className="flex-1">{errorMessage}</span>
              </div>
            )}

            {/* Prompt Textarea */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Chủ đề, Nội dung bài học hoặc Yêu cầu chi tiết
              </label>
              <textarea
                rows={4}
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                placeholder="VD: Tạo 5 câu hỏi về Định luật II Newton, công thức tính gia tốc, lực ma sát... hoặc dán đoạn văn bản cần tạo câu hỏi vào đây..."
                className="w-full bg-slate-50 border border-slate-300 rounded-xl p-3.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-600 focus:bg-white transition-all font-normal"
              />
            </div>

            {/* File đính kèm */}
            <div className="bg-slate-50/70 border border-slate-200 rounded-2xl p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider">
                    Tài liệu & Hình ảnh tham khảo
                  </label>
                  <p className="text-[11px] text-slate-500">
                    File đính kèm sẽ được lưu link tĩnh bảo mật để Gemini phân tích
                  </p>
                </div>
                {selectedFiles.length > 0 && (
                  <button
                    type="button"
                    onClick={handleClearAllFiles}
                    className="text-[11px] text-rose-600 hover:text-rose-700 font-semibold cursor-pointer"
                  >
                    Xóa tất cả ({selectedFiles.length})
                  </button>
                )}
              </div>

              {/* Action Buttons: Chọn Thư Viện & Tải file mới */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <button
                  type="button"
                  onClick={() => setIsLibraryModalOpen(true)}
                  className="px-4 py-3 bg-white hover:bg-purple-50/50 border border-purple-200 hover:border-purple-400 rounded-xl text-xs font-bold text-purple-800 shadow-2xs flex items-center justify-center space-x-2 transition-all cursor-pointer group"
                >
                  <Layers className="w-4 h-4 text-purple-600 group-hover:scale-110 transition-transform" />
                  <span>Chọn từ thư viện (2 Tabs)</span>
                </button>

                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingMedia}
                  className="px-4 py-3 bg-white hover:bg-indigo-50/50 border border-indigo-200 hover:border-indigo-400 rounded-xl text-xs font-bold text-indigo-800 shadow-2xs flex items-center justify-center space-x-2 transition-all cursor-pointer group disabled:opacity-50"
                >
                  {uploadingMedia ? (
                    <Loader2 className="w-4 h-4 text-indigo-600 animate-spin" />
                  ) : (
                    <UploadCloud className="w-4 h-4 text-indigo-600 group-hover:scale-110 transition-transform" />
                  )}
                  <span>{uploadingMedia ? 'Đang tải lên...' : 'Tải lên từ máy (Ảnh & Docs)'}</span>
                </button>

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="application/pdf,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg,image/webp"
                  onChange={handleFilesChange}
                  className="hidden"
                />
              </div>

              {/* File list */}
              {selectedFiles.length > 0 && (
                <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1 pt-1">
                  {selectedFiles.map((fileItem) => {
                    const isDoc = fileItem.type === 'doc' || fileItem.name.toLowerCase().endsWith('.pdf') || fileItem.name.toLowerCase().endsWith('.docx');
                    return (
                      <div 
                        key={fileItem.id}
                        className="p-2.5 bg-white border border-slate-200/90 rounded-xl flex items-center justify-between gap-2 shadow-2xs hover:border-purple-300 transition-colors"
                      >
                        <div className="flex items-center space-x-2.5 min-w-0">
                          {isDoc ? (
                            <div className="w-7 h-7 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center shrink-0 border border-rose-100">
                              <FileText className="w-4 h-4" />
                            </div>
                          ) : (
                            <div className="w-7 h-7 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center shrink-0 border border-purple-100">
                              <ImageIcon className="w-4 h-4" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="flex items-center space-x-1.5">
                              <span className="text-xs font-semibold text-slate-800 truncate max-w-[220px] sm:max-w-xs block">
                                {fileItem.name}
                              </span>
                              <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded-md ${
                                fileItem.source === 'library' 
                                  ? 'bg-blue-100 text-blue-700' 
                                  : 'bg-emerald-100 text-emerald-700'
                              }`}>
                                {fileItem.source === 'library' ? 'Thư viện' : 'Tải lên'}
                              </span>
                            </div>
                            <span className="text-[10px] text-slate-400 block">
                              {formatFileSize(fileItem.size)}
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(fileItem.id)}
                          className="p-1.5 text-slate-400 hover:text-rose-600 rounded-lg hover:bg-rose-50 transition-colors shrink-0 cursor-pointer"
                          title="Xóa file này"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Parameters */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Số lượng câu hỏi
                </label>
                <input
                  type="number"
                  min={1}
                  max={maxAllowed > 0 ? maxAllowed : 50}
                  value={questionCount}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    setQuestionCount(isNaN(val) ? 1 : Math.max(1, Math.min(maxAllowed > 0 ? maxAllowed : 50, val)));
                  }}
                  placeholder={`VD: ${Math.min(5, maxAllowed > 0 ? maxAllowed : 5)}`}
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl py-2.5 px-3 text-xs sm:text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-600 transition-all font-semibold"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Mức độ tư duy
                </label>
                <select
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl py-2.5 px-3 text-xs sm:text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-600 transition-all font-medium"
                >
                  <option value="Hỗn hợp">Hỗn hợp (Nhận biết - Vận dụng)</option>
                  <option value="Nhận biết">Nhận biết</option>
                  <option value="Thông hiểu">Thông hiểu</option>
                  <option value="Vận dụng">Vận dụng</option>
                  <option value="Vận dụng cao">Vận dụng cao</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  GEMINI MODEL
                </label>
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  disabled={uploadingMedia || isStartingJob}
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl py-2.5 px-3 text-xs sm:text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-600 transition-all font-mono font-bold cursor-pointer disabled:opacity-60"
                >
                  {availableModels.map((m) => (
                    <option key={m} value={m}>
                      {m} {m === 'gemini-2.5-flash' ? '⚡ (Mặc định)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Footer buttons */}
            <div className="flex items-center justify-between gap-2.5 pt-4 border-t border-slate-100">
              <div className="text-[11px] text-slate-500">
                <span>Khởi tạo tiến trình chạy ngầm qua SSE</span>
              </div>

              <div className="flex items-center space-x-2.5 justify-end">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleClose}
                  disabled={uploadingMedia || isStartingJob}
                >
                  Hủy
                </Button>

                {/* NÚT BẤM DUY NHẤT: "Tạo câu hỏi" */}
                <Button
                  type="button"
                  onClick={handleStartGeneration}
                  disabled={uploadingMedia || isStartingJob}
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow-md flex items-center space-x-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm font-bold py-2.5 px-5"
                  title="Khởi chạy tiến trình AI tạo câu hỏi"
                >
                  {isStartingJob ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-1" />
                      <span>Đang khởi tạo...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-1" />
                      <span>Tạo câu hỏi</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <AiMediaLibraryModal
        isOpen={isLibraryModalOpen}
        onClose={() => setIsLibraryModalOpen(false)}
        onConfirm={handleSelectFromLibrary}
      />
    </div>
  );
};
